import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from '../../components/shared/Layout';
import { Card, PageHeader, Button, Badge } from '../../components/shared/ui';
import { adminAPI } from '../../utils/api';
import AdminCompanies from './Companies';
import AdminStudents from './Students';
import AuditLogs from './AuditLogs';
import SystemHealth from './SystemHealth';
import AppStats from './AppStats';
import AdminAdmins from './Admins';
import Analytics from './Analytics';
import AssignmentPanel from './AssignmentPanel';
import AdminMessages from './Messages';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';

const StatCard = ({ label, value, sub, progress, tone='blue' }) => {
  const tones = { blue:'text-brand-700', violet:'text-violet-600', cyan:'text-cyan-600', gray:'text-ink-700', green:'text-emerald-600', amber:'text-amber-600' };
  const bars  = { blue:'bg-brand-600', violet:'bg-violet-500', cyan:'bg-cyan-500', green:'bg-emerald-500', amber:'bg-amber-500', gray:'bg-ink-400' };
  return (
    <Card>
      <div className={`text-2xl sm:text-3xl font-bold ${tones[tone]}`}>{value ?? '...'}</div>
      <div className="text-xs sm:text-sm text-ink-500 mt-1">{label}</div>
      {sub !== undefined && (
        <div className="text-xs text-ink-400 mt-0.5">{sub}</div>
      )}
      {progress !== undefined && (
        <div className="mt-2">
          <div className="w-full bg-ink-100 rounded-full h-1.5">
            <div className={`${bars[tone]} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
          <div className="text-xs text-ink-400 mt-1">%{progress} tamamlanma</div>
        </div>
      )}
    </Card>
  );
};

const DEPT_COLORS = ['#2563eb','#7c3aed','#0891b2','#059669','#d97706','#dc2626','#6b7280','#1d4ed8'];

const Overview = () => {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [approving, setApproving] = useState(null);

  useEffect(() => {
    adminAPI.getStats().then(r => setStats(r.data)).catch(() => {});
    adminAPI.getStatsTrends().then(r => setTrends(r.data)).catch(() => {});
    adminAPI.getCompanies({ approved: 'false' }).then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const handleApprove = async (id) => {
    setApproving(id);
    await adminAPI.approveCompany(id);
    setCompanies(c => c.filter(x => x.id !== id));
    setApproving(null);
    adminAPI.getStats().then(r => setStats(r.data));
  };

  const regChartData = (() => {
    if (!trends?.registrations) return [];
    const map = {};
    trends.registrations.forEach(r => {
      if (!map[r.month]) map[r.month] = { month: r.month, student: 0, company: 0 };
      if (r.role === 'student') map[r.month].student = +r.count;
      if (r.role === 'company') map[r.month].company = +r.count;
    });
    return Object.values(map).sort((a,b) => a.month.localeCompare(b.month));
  })();

  const deptData = (trends?.departments || []).map(d => ({ name: d.department, value: +d.count }));

  return (
    <>
      <PageHeader title="Admin Paneli" description="Sistem geneli istatistikler" />

      {/* Öğrenci istatistikleri */}
      <div className="mb-2">
        <div className="text-xs font-semibold text-ink-400 uppercase tracking-wider mb-2 px-0.5">Öğrenciler</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <StatCard
            label="Toplam Kayıt"
            value={stats?.total_students}
            sub="Sisteme kayıtlı öğrenci"
            tone="blue"
          />
          <StatCard
            label="Profil Tamamlayan"
            value={stats?.students}
            sub={stats ? `${stats.total_students - stats.students} öğrencinin profili eksik` : undefined}
            progress={stats && stats.total_students > 0 ? Math.round(stats.students / stats.total_students * 100) : undefined}
            tone="green"
          />
          <StatCard
            label="Eksik Profilli"
            value={stats ? stats.total_students - stats.students : undefined}
            sub="Profili tamamlanmamış"
            tone="amber"
          />
        </div>
      </div>

      {/* Platform istatistikleri */}
      <div className="mb-6 mt-4">
        <div className="text-xs font-semibold text-ink-400 uppercase tracking-wider mb-2 px-0.5">Platform</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Onaylı Şirket"   value={stats?.companies}          tone="violet" />
          <StatCard label="Aktif İlan"      value={stats?.active_positions}   tone="cyan" />
          <StatCard label="Toplam Başvuru"  value={stats?.total_applications} tone="gray" />
          <StatCard label="≥%75 Uyum"       value={stats?.high_matches}       tone="green" />
        </div>
      </div>

      {stats?.pending_approvals > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="font-medium text-amber-900">{stats.pending_approvals} şirket onay bekliyor</div>
            <Button as="a" href="/admin/companies" className="!bg-amber-600 hover:!bg-amber-700">İncele</Button>
          </div>
        </Card>
      )}

      {trends && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {regChartData.length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-ink-900 mb-4">Aylık Kayıt Trendi</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={regChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="student" name="Öğrenci" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="company" name="Şirket"  stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
          {deptData.length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-ink-900 mb-4">Bölüm Dağılımı</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={deptData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip />
                  <Bar dataKey="value" name="Öğrenci" radius={[0,4,4,0]}>
                    {deptData.map((_,i) => <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      <Card>
        <h2 className="text-base font-semibold text-ink-900 mb-3 flex items-center gap-2">
          Onay Bekleyen Şirketler
          {companies.length > 0 && <Badge tone="red">{companies.length}</Badge>}
        </h2>
        {companies.length === 0 ? (
          <div className="text-center py-8 text-ink-400">
            <div className="text-2xl mb-2">✓</div>
            <p className="text-sm">Onay bekleyen şirket yok.</p>
          </div>
        ) : companies.map(c => (
          <div key={c.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-ink-100 last:border-0">
            <div className="min-w-0">
              <div className="font-medium text-sm text-ink-900 truncate">{c.name}</div>
              <div className="text-xs text-ink-500 truncate">
                {c.email} · {c.sector} · {c.city} · Kayıt: {new Date(c.registered_at).toLocaleDateString('tr-TR')}
              </div>
            </div>
            <Button onClick={() => handleApprove(c.id)} disabled={approving === c.id} className="!bg-emerald-600 hover:!bg-emerald-700 sm:w-auto w-full">
              {approving === c.id ? 'Onaylanıyor...' : 'Onayla'}
            </Button>
          </div>
        ))}
      </Card>
    </>
  );
};

const AdminDashboard = () => (
  <Layout>
    <Routes>
      <Route index element={<Overview />} />
      <Route path="companies"  element={<AdminCompanies />} />
      <Route path="students"   element={<AdminStudents />} />
      <Route path="audit-logs" element={<AuditLogs />} />
      <Route path="health"     element={<SystemHealth />} />
      <Route path="app-stats"  element={<AppStats />} />
      <Route path="analytics"  element={<Analytics />} />
      <Route path="admins"     element={<AdminAdmins />} />
      <Route path="atama"      element={<AssignmentPanel />} />
      <Route path="mesaj"      element={<AdminMessages />} />
    </Routes>
  </Layout>
);

export default AdminDashboard;
